import React, { useState, useEffect } from 'react';
import { ref, push, onValue, update, remove } from 'firebase/database';
import { ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../firebase';

function CRUDSection({ category, config }) {
  const [items, setItems] = useState([]);
  const [formData, setFormData] = useState({});
  const [editId, setEditId] = useState('');
  const [imageFile, setImageFile] = useState(null);
  const [status, setStatus] = useState({ message: '', isError: false });
  const [isEditing, setIsEditing] = useState(false);
  const [draggedItem, setDraggedItem] = useState(null);

  useEffect(() => {
    const itemsRef = ref(db, category);
    const unsubscribe = onValue(itemsRef, (snapshot) => {
      const data = snapshot.val();
      if (!data) {
        setItems([]);
        return;
      }
      
      const itemsArray = Object.entries(data)
        .map(([id, item]) => ({ id, ...item }))
        .sort((a, b) => {
          // Sort by order first, then by timestamp for items without order
          if (a.order !== undefined && b.order !== undefined) {
            return a.order - b.order;
          }
          if (a.order !== undefined) return -1;
          if (b.order !== undefined) return 1;
          return (b.timestamp || 0) - (a.timestamp || 0);
        });
      
      setItems(itemsArray);
    });

    return () => unsubscribe();
  }, [category]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleImageChange = (e) => {
    setImageFile(e.target.files[0]);
  };

  const uploadImage = async (file) => {
    if (!file) return null;
    const fileName = `${category}/${Date.now()}_${file.name}`;
    const imageRef = storageRef(storage, fileName);
    const snapshot = await uploadBytes(imageRef, file);
    return await getDownloadURL(snapshot.ref);
  };

  const showStatus = (message, isError = false) => {
    setStatus({ message, isError });
    setTimeout(() => setStatus({ message: '', isError: false }), 3000);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const data = {
        ...formData,
        timestamp: Date.now()
      };

      if (editId) {
        // Update existing
        if (imageFile) {
          data.imageUrl = await uploadImage(imageFile);
        }
        await update(ref(db, `${category}/${editId}`), data);
        showStatus('Updated successfully!');
      } else {
        // Create new - assign order as the last position
        if (!imageFile) {
          alert('Please select an image');
          return;
        }
        data.imageUrl = await uploadImage(imageFile);
        data.order = items.length; // New items go to the end
        await push(ref(db, category), data);
        showStatus('Added successfully!');
      }

      resetForm();
    } catch (error) {
      showStatus('Error: ' + error.message, true);
    }
  };

  const handleEdit = (item) => {
    setEditId(item.id);
    setIsEditing(true);
    const { id, imageUrl, timestamp, ...editableData } = item;
    setFormData(editableData);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this item?')) {
      try {
        await remove(ref(db, `${category}/${id}`));
        showStatus('Deleted successfully!');
      } catch (error) {
        showStatus('Error: ' + error.message, true);
      }
    }
  };

  const resetForm = () => {
    setFormData({});
    setEditId('');
    setImageFile(null);
    setIsEditing(false);
    const form = document.getElementById(`${category}-form`);
    if (form) form.reset();
  };

  // Drag and Drop handlers
  const handleDragStart = (e, item, index) => {
    setDraggedItem({ item, index });
    e.dataTransfer.effectAllowed = 'move';
    e.currentTarget.style.opacity = '0.5';
  };

  const handleDragEnd = (e) => {
    e.currentTarget.style.opacity = '1';
    setDraggedItem(null);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = async (e, targetItem, targetIndex) => {
    e.preventDefault();
    
    if (!draggedItem || draggedItem.index === targetIndex) return;

    const newItems = [...items];
    const draggedElement = newItems[draggedItem.index];
    
    // Remove from old position
    newItems.splice(draggedItem.index, 1);
    // Insert at new position
    newItems.splice(targetIndex, 0, draggedElement);

    // Update the order field for all items
    try {
      const updates = {};
      newItems.forEach((item, index) => {
        updates[`${category}/${item.id}/order`] = index;
      });
      
      await update(ref(db), updates);
      showStatus('Order updated successfully!');
    } catch (error) {
      showStatus('Error updating order: ' + error.message, true);
    }
  };

  return (
    <div className="tab-content active">
      <div className="form-section">
        <h2>{isEditing ? 'Update' : 'Add New'} {config.title}</h2>
        {status.message && (
          <div className={`status-msg ${status.isError ? 'error' : 'success'}`}>
            {status.message}
          </div>
        )}
        <form id={`${category}-form`} onSubmit={handleSubmit}>
          {config.renderForm(formData, handleInputChange, handleImageChange, isEditing)}
          <div className="form-actions">
            <button type="submit" className="btn btn-primary">
              {isEditing ? 'Update' : 'Submit'}
            </button>
            {isEditing && (
              <button 
                type="button" 
                className="btn btn-secondary" 
                onClick={resetForm}
              >
                Cancel
              </button>
            )}
          </div>
        </form>
      </div>

      <div className="table-section">
        <h3>All {config.title}s</h3>
        <p className="drag-instruction">💡 Drag and drop rows to reorder</p>
        <table className="data-table">
          <thead>
            <tr>
              <th className="drag-handle-header">Order</th>
              {config.tableColumns.map((col, idx) => (
                <th key={idx}>{col}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {items.length === 0 ? (
              <tr>
                <td colSpan={config.tableColumns.length + 1} className="empty-state">
                  No data yet
                </td>
              </tr>
            ) : (
              items.map((item, index) => (
                <tr
                  key={item.id}
                  draggable
                  onDragStart={(e) => handleDragStart(e, item, index)}
                  onDragEnd={handleDragEnd}
                  onDragOver={handleDragOver}
                  onDrop={(e) => handleDrop(e, item, index)}
                  className="draggable-row"
                >
                  <td className="drag-handle">
                    <span className="drag-icon">⋮⋮</span>
                  </td>
                  {config.renderRow(item, handleEdit, handleDelete)}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default CRUDSection;
